import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import useCart from 'hooks/use-cart';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import type { Dispensaries } from 'src/gql/graphql';
import { isStorefrontCheckoutThemeRoute } from './checkout-theme-routes';

export function selectStorefrontCheckoutDispensary(
  routeCName: string | undefined,
  routeDispensary: Dispensaries | null | undefined,
  cartDispensary: Dispensaries | undefined,
  allowMatchingCart = true
): Dispensaries | undefined {
  if (!routeCName) {
    return undefined;
  }

  if (routeDispensary?.cName === routeCName) {
    return routeDispensary;
  }

  if (allowMatchingCart && cartDispensary?.cName === routeCName) {
    return cartDispensary;
  }

  return undefined;
}

type StorefrontCheckoutDispensaryState = {
  dispensary: Dispensaries | undefined;
  error: unknown;
  loading: boolean;
  routeCName: string | undefined;
};

/** Resolves storefront-checkout identity without trusting a stale or unrelated persisted cart. */
export function useStorefrontCheckoutDispensary({
  allowMatchingCart = true,
}: { allowMatchingCart?: boolean } = {}): StorefrontCheckoutDispensaryState {
  const router = useRouter();
  const Cart = useCart();
  const { dispensary: routeDispensary, error, loading } = useCurrentDispensary();
  const cartDispensary = useObserver(() => Cart?.dispensary) as Dispensaries | undefined;
  const rawRouteCName = isStorefrontCheckoutThemeRoute(router?.route) ? router?.query.cName : undefined;
  const routeCName = Array.isArray(rawRouteCName) ? rawRouteCName[0] : rawRouteCName;
  const dispensary = selectStorefrontCheckoutDispensary(routeCName, routeDispensary, cartDispensary, allowMatchingCart);

  return { dispensary, error, loading, routeCName };
}
